import type { CccdTeamStatus } from '@/lib/api/cccdAdminClient';
import { TEAM_STATUS_FILTERS, TEAM_STATUS_FILTER_LABELS } from '@/lib/cccdTeamStatus';
import { ModuleToolbar } from '@/components/admin/ModuleLayout';
import DataTable, { type DataTableColumn, type DataTableSort } from '@/components/ui/table/DataTable';

// Toolbar + tabla, compartido por el tab Equipos de EventDetailPage y
// RegionalDetailPage — mismo componente para ambos tipos de evento (ver
// cccd-ux-redesign: nacional y regional deben verse iguales) y el mismo
// ModuleToolbar/DataTable que cualquier otro listado del gestor, para que
// esta tabla no se vea distinta al resto.
export function TeamsListCard<T>({
  columns,
  rows,
  rowKey,
  loading,
  statusFilter,
  onStatusFilterChange,
  onCreateTeam,
  onDownloadTemplate,
  onImportTeams,
  search,
  onSearchChange,
  onRefresh,
  refreshing,
  sort,
  onSortChange,
  page,
  pageSize,
  total,
  onPageChange,
}: {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  statusFilter: CccdTeamStatus | 'all';
  onStatusFilterChange: (status: CccdTeamStatus | 'all') => void;
  onCreateTeam: () => void;
  // cccd-teams-institutions-csv-import: ambos opcionales — algunos consumidores
  // de TeamsListCard (ninguno hoy) podrían no soportar import masivo.
  onDownloadTemplate?: () => void;
  onImportTeams?: () => void;
  /** Búsqueda por nombre de equipo — opcional para no forzar a cada caller a implementarla ya. */
  search?: string;
  onSearchChange?: (value: string) => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  sort?: DataTableSort;
  onSortChange?: (sort: DataTableSort) => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="space-y-4">
      <ModuleToolbar
        toolbar={{
          search: onSearchChange
            ? { value: search ?? '', onChange: onSearchChange, placeholder: 'Buscar equipo...' }
            : undefined,
          onRefresh,
          refreshing,
          filters: [
            {
              type: 'select',
              key: 'status',
              label: 'Estado',
              value: statusFilter,
              onChange: (value) => onStatusFilterChange(value as CccdTeamStatus | 'all'),
              options: TEAM_STATUS_FILTERS.map((s) => ({ label: TEAM_STATUS_FILTER_LABELS[s], value: s })),
            },
          ],
          extra: onDownloadTemplate ? (
            <button
              type="button"
              onClick={onDownloadTemplate}
              className="min-h-11 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/[0.04]"
            >
              Descargar plantilla CSV
            </button>
          ) : undefined,
          onImport: onImportTeams,
          onCreate: onCreateTeam,
          createLabel: '+ Nuevo equipo',
        }}
      />
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={rowKey}
        loading={loading}
        emptyMessage="No hay equipos con este filtro."
        sort={sort}
        onSortChange={onSortChange}
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={onPageChange}
      />
    </div>
  );
}
